import {
  CLIENT_ACTION_VALUES,
  COVERAGE_FORM,
  RFP_ACTION_VALUES,
} from "@platform/rfp-core";

import { submitEvent } from "../../../utils/tracking";
import { RFP_ACTION_TYPES, RFP_STEPS } from "../constants";
import {
  coverages,
  initDetailsValues,
} from "../coverages/CoverageForms/coverages";
import {
  coveragesValidations,
  validation,
} from "../coverages/CoverageForms/coveragesValidations";
import {
  buildDocumentTypes,
  buildUploadedDocuments,
  demoteCensusToOptional,
  sortProductsByCoverage,
} from "../utils";

import { isRfpFormValid, rfpInfoValidations } from "./validations";

const {
  UPDATE_COVERAGES,
  UPDATE_NEXT_STEP,
  UPDATE_BACK_STEP,
  UPDATE_STEP,
  UPDATE_SELECTED_COVERAGES,
  UPDATE_SELECTED_COVERAGE,
  UPDATE_SELECTED_CARRIERS,
  UPDATE_CENTRO_SELECTED_CARRIER,
  UPDATE_DOCUMENT,
  SET_DOCUMENTS,
  PREPARE_RFP,
  UPDATE_COVERAGE_INDEX,
  HANDLE_BACK_COVERAGE,
  HANDLE_NEXT_VALIDATE_COVERAGE,
  UPDATE_SELECTED_COVERAGE_WITH_DOCUMENTS,
  UPDATE_LAST_MODIFIED,
  UPDATE_RFP_ID,
  UPDATE_FIELD,
  CLEANUP_DOCUMENTS,
  UPDATE_STATE_RELATED_DOCUMENTS,
  UPDATE_STATE_UPLOAD_DOCUMENTS_WITHOUT_COVERAGE,
  UPDATE_STILL_PENDING_DOC,
  UPDATE_ADDITIONAL_DOCUMENTS,
  REMOVE_ADDITIONAL_DOCUMENTS,
  EDIT_STATE_RELATED_DOCUMENTS,
  REMOVE_STATE_RELATED_DOCUMENTS,
  REMOVE_STATE_RELATED_DOCUMENTS_ALL_COVERAGES,
  UPDATE_STATE_DELETE_DOCUMENT,
} = RFP_ACTION_TYPES;

const {
  ID,
  ACTIVE_STEP,
  HIGHER_LAST_STEP,
  COVERAGES,
  NEW_COVERAGES,
  CURRENT_COVERAGES,
  CARRIERS,
  SELECTED_COVERAGES,
  SELECTED_CARRIERS,
  DOCUMENTS,
  COVERAGE_INDEX,
  ARE_COVERAGES_VALID,
  LAST_MODIFIED,
  STILL_PENDING_DOC,
  INIT_DOCUMENTS,
  DOCUMENT_TYPES,
  ARE_DOCUMENTS_VALID,
  UPLOADED_FILES,
  ADDITIONAL_DOCUMENTS,
  IS_CENTRO_CARRIER_SELECTED,
} = RFP_ACTION_VALUES;

const { IS_CURRENT_COVERAGE } = COVERAGE_FORM;

const {
  IS_CURRENT_CLIENT,
  FORM_INFO,
  CLIENT_ERROR,
  IS_RFP_FORM_VALID,
  DUE_DATE,
  EFFECTIVE_DATE,
  HAS_BEN_ADMIN_SYSTEM_STATUS,
  REQUESTED_SYSTEM,
  REQUESTED_SYSTEM_OTHER,
  REQUESTED_AMOUNT,
  TECH_IMPLEMENTATION,
  TECH_CREDIT_TYPE,
  TECH_CREDIT_AMOUNT,
  MARKETING_OBJECTIVES,
  EN_CENSUS_AVAILABLE,
  EN_CENSUS_FEATURE_ENABLED,
  EN_CENSUS_FOR_PROSPECTS,
  EN_PUSH_SOLD_PLAN_DATA,
} = CLIENT_ACTION_VALUES;

const handleCoverageSelection = (state, payload) => {
  let newData = [];
  const selectedList = state[SELECTED_COVERAGES] || [];
  const isAlreadySelected = selectedList.some(
    ({ name }) => name === payload.name,
  );
  if (isAlreadySelected || payload.delete) {
    newData = selectedList.filter((v) => v.name !== payload.name);
  } else {
    newData = [...selectedList, payload];
  }

  const getIndex = (c) => state[COVERAGES].findIndex(({ id }) => id === c.id);
  newData?.sort((a, b) => getIndex(a) - getIndex(b));

  return newData;
};

const handleCarrierSelection = (state, payload) => {
  let selectedList = state[SELECTED_CARRIERS] || [];
  const isAlreadySelected = selectedList?.some((v) => v === payload.name);
  if (!isAlreadySelected) {
    selectedList.push(payload.name);
  } else if (payload.delete) {
    selectedList = selectedList.filter((v) => v !== payload.name);
  }
  return selectedList;
};

const shouldDemoteCensusToOptional = (state) => {
  const isEnCensusFeatureEnabled = Boolean(
    state[FORM_INFO]?.[EN_CENSUS_FEATURE_ENABLED],
  );
  const benAdminInPlace =
    state[FORM_INFO]?.[HAS_BEN_ADMIN_SYSTEM_STATUS] === "1";
  const isCurrentClient = state[IS_CURRENT_CLIENT] === true;
  const censusForProspects =
    state[FORM_INFO]?.[EN_CENSUS_FOR_PROSPECTS] === true;
  const isClientEligible = isCurrentClient || censusForProspects;
  const requestedSystem = state[FORM_INFO]?.[REQUESTED_SYSTEM] ?? "";
  const enCensusAvailable = state[FORM_INFO]?.[EN_CENSUS_AVAILABLE] ?? "";

  return (
    isEnCensusFeatureEnabled &&
    benAdminInPlace &&
    isClientEligible &&
    requestedSystem === "employeeNavigator" &&
    enCensusAvailable === "yes"
  );
};

export const initState = {
  [ID]: null,
  [ACTIVE_STEP]: 0,
  [HIGHER_LAST_STEP]: 0,
  [ARE_COVERAGES_VALID]: false,
  [COVERAGE_INDEX]: 0,
  [COVERAGES]: [],
  [NEW_COVERAGES]: [],
  [CURRENT_COVERAGES]: [],
  [CARRIERS]: [],
  [SELECTED_COVERAGES]: [],
  [SELECTED_CARRIERS]: [],
  [IS_CENTRO_CARRIER_SELECTED]: false,
  [DOCUMENTS]: {
    files: [],
  },
  [STILL_PENDING_DOC]: false,
  [LAST_MODIFIED]: null,
  [FORM_INFO]: {
    [DUE_DATE]: null,
    [EFFECTIVE_DATE]: null,
    [HAS_BEN_ADMIN_SYSTEM_STATUS]: "1",
    [REQUESTED_SYSTEM]: "",
    [REQUESTED_SYSTEM_OTHER]: "",
    [TECH_IMPLEMENTATION]: "0",
    [TECH_CREDIT_TYPE]: "",
    [TECH_CREDIT_AMOUNT]: 0,
    [MARKETING_OBJECTIVES]: "",
    [REQUESTED_AMOUNT]: "",
    [EN_CENSUS_AVAILABLE]: "",
    [EN_PUSH_SOLD_PLAN_DATA]: "",
  },
  [INIT_DOCUMENTS]: [],
  [ARE_DOCUMENTS_VALID]: false,
  [ADDITIONAL_DOCUMENTS]: [],
};

const newCoverages = (currentCoverages, coverage) =>
  currentCoverages?.map((cov) => (cov.id === coverage.id ? coverage : cov));

const mergeArrays = (a, b) =>
  a?.filter(({ name }) => !b?.some((i) => i.name === name));

export const areCoveragesValid = (coverages) =>
  !coverages.some((c) => isCoverageInvalid(c));

export const isCoverageInvalid = (coverage) =>
  !!coverage.details?.plans?.some((p) => {
    const validate = coveragesValidations[coverage.name];
    if (!validate) return false;
    const coverageErrors = Object.values(
      validate({
        ...p,
        [IS_CURRENT_CLIENT]: coverage[IS_CURRENT_CLIENT],
        [IS_CURRENT_COVERAGE]: coverage[IS_CURRENT_COVERAGE],
      }),
    );
    return !!coverageErrors && coverageErrors?.some((v) => !!v);
  });

const submitStepEvent = (step, rfpId) => {
  switch (step) {
    case 0:
      submitEvent("RFP Details Step Viewed", {
        rfp_id: rfpId,
      });
      break;
    case 1:
      submitEvent("RFP Select Coverages Step Viewed", {
        rfp_id: rfpId,
      });
      break;
    case 2:
      submitEvent("RFP Add Coverage Details Step Viewed", {
        rfp_id: rfpId,
      });
      break;
    case 3:
      submitEvent("RFP Select Carriers Step Viewed", {
        rfp_id: rfpId,
      });
      break;
    case 4:
      submitEvent("RFP Review Step Viewed", {
        rfp_id: rfpId,
      });
      break;
    case 5:
      submitEvent("RFP Upload Documents Step Viewed", {
        rfp_id: rfpId,
      });
      break;
    default:
      break;
  }
};

const dependentValidations = {
  [TECH_CREDIT_TYPE]: [TECH_CREDIT_AMOUNT],
};

// TODO(eslint): fix lint error
// eslint-disable-next-line complexity, sonarjs/cognitive-complexity
const RfpReducer = (state, action) => {
  const { type, value } = action;

  switch (type) {
    case UPDATE_STILL_PENDING_DOC: {
      return {
        ...state,
        [STILL_PENDING_DOC]: value,
      };
    }
    case UPDATE_NEXT_STEP: {
      const currStep = state[ACTIVE_STEP] + 1;
      const currHigherStep = state[HIGHER_LAST_STEP];

      submitStepEvent(currStep, state[ID]);

      const newState = {
        ...state,
        [ARE_COVERAGES_VALID]: areCoveragesValid(state[SELECTED_COVERAGES]),
        [ACTIVE_STEP]: currStep,
        [HIGHER_LAST_STEP]:
          currStep > currHigherStep ? currStep : currHigherStep,
      };
      if (state[STILL_PENDING_DOC]) {
        newState[STILL_PENDING_DOC] = false;
      }

      return newState;
    }
    case UPDATE_STEP: {
      submitStepEvent(value, state[ID]);
      const newState = {
        ...state,
        [ACTIVE_STEP]: value,
        [HIGHER_LAST_STEP]:
          value > state[HIGHER_LAST_STEP] ? value : state[HIGHER_LAST_STEP],
        [COVERAGE_INDEX]: action[COVERAGE_INDEX]?.toString()
          ? action[COVERAGE_INDEX]
          : state[COVERAGE_INDEX],
      };

      if (value !== RFP_STEPS.DOCUMENTS && state[STILL_PENDING_DOC]) {
        newState[STILL_PENDING_DOC] = false;
      }

      return newState;
    }
    case UPDATE_BACK_STEP: {
      let backStep = 0;
      if (state[ACTIVE_STEP] === RFP_STEPS.RFP_FORM || state[ACTIVE_STEP] < 0) {
        value.handleBackNavigation();
      } else if (
        state[ACTIVE_STEP] === RFP_STEPS.SELECTED_CARRIERS &&
        state[SELECTED_COVERAGES].length === 0
      ) {
        backStep = 1;
      } else {
        backStep = state[ACTIVE_STEP] - 1;
      }

      const newState = {
        ...state,
        [ACTIVE_STEP]: backStep,
      };
      if (state[STILL_PENDING_DOC]) {
        newState[STILL_PENDING_DOC] = false;
      }

      submitStepEvent(backStep, state[ID]);

      return newState;
    }
    case PREPARE_RFP: {
      const currentClientProducts = value[CURRENT_COVERAGES] || [];
      const coverages = value[COVERAGES].reduce(
        (acc, c) => {
          const clientProd = currentClientProducts.find((cc) => cc.id === c.id);
          if (clientProd) {
            acc[CURRENT_COVERAGES].push({
              ...c,
              clientProductCarrierIds: clientProd.clientProductCarrierIds || [],
            });
          } else {
            acc[NEW_COVERAGES].push(c);
          }
          return acc;
        },
        { [CURRENT_COVERAGES]: [], [NEW_COVERAGES]: [] },
      );

      const formInfo = { ...state[FORM_INFO], ...value[FORM_INFO] };
      const currentSortedCoverages = sortProductsByCoverage(
        coverages[CURRENT_COVERAGES],
      );
      const newSortedCovergaes = sortProductsByCoverage(
        coverages[NEW_COVERAGES],
      );
      return {
        ...state,
        ...value,
        [FORM_INFO]: formInfo,
        [IS_RFP_FORM_VALID]: validateForm(formInfo, isRfpFormValid),
        [CURRENT_COVERAGES]: currentSortedCoverages,
        [NEW_COVERAGES]: newSortedCovergaes,
        [DOCUMENTS]: { files: value.rfpFiles || [] },
      };
    }
    case UPDATE_COVERAGES: {
      const currentCoverages = mergeArrays(
        state[CURRENT_COVERAGES],
        value[CURRENT_COVERAGES],
      );

      return {
        ...state,
        [COVERAGES]: value[COVERAGES],
        [CURRENT_COVERAGES]: currentCoverages,
        [NEW_COVERAGES]: value[COVERAGES].filter(
          (c) => !currentCoverages.some((cc) => cc.id === c.id),
        ),
      };
    }
    case UPDATE_SELECTED_COVERAGE: {
      const coverage = { ...value };

      const currentPlan = coverage?.details?.plans[action.currentPlanKey];

      if (action.fieldKey) {
        if (
          currentPlan.errors &&
          validation[action.fieldKey] &&
          !validation[action.fieldKey]({
            ...currentPlan,
            [IS_CURRENT_CLIENT]: state[IS_CURRENT_CLIENT],
          })
        ) {
          delete currentPlan.errors[action.fieldKey];
        }
      }

      const selectedCoverages = newCoverages(state[SELECTED_COVERAGES], {
        ...coverage,
        isValid: !isCoverageInvalid(coverage),
      });

      const { documentTypes: rawDocTypes, uploadedFiles } = buildDocumentTypes(
        selectedCoverages,
        state[IS_CURRENT_CLIENT],
        state[DOCUMENTS].files,
        state.draft,
      );
      const enCensus = shouldDemoteCensusToOptional(state);
      const documentTypes = enCensus
        ? demoteCensusToOptional(rawDocTypes)
        : rawDocTypes;
      const areValid = documentTypes.every((doc) =>
        doc.coverages.required.every((item) => item.files?.length),
      );
      return {
        ...state,
        [ARE_COVERAGES_VALID]: areCoveragesValid(selectedCoverages),
        [SELECTED_COVERAGES]: selectedCoverages,
        [ARE_DOCUMENTS_VALID]: areValid,
        [DOCUMENT_TYPES]: documentTypes,
        [UPLOADED_FILES]: uploadedFiles,
      };
    }
    case UPDATE_STATE_UPLOAD_DOCUMENTS_WITHOUT_COVERAGE: {
      const { uploadedData } = value;

      const newUploadedFiles = uploadedData.reduce((acc, item) => {
        const documentName = item.name || item.filename;
        const docObj = {
          documentTypes: {},
          fileName: documentName,
          file: {
            ...item,
            name: documentName,
            downloadLink: item.url,
            infected: false,
          },
        };
        if (item.types.length) {
          docObj["documentTypes"] = item.types.reduce(
            (accumulator, typeItem) => ({
              ...accumulator,
              [typeItem]: [],
            }),
            {},
          );
        }
        acc.push(docObj);
        return acc;
      }, []);
      const existingUploadedData = [...state[UPLOADED_FILES]];
      const uploadedFiles = buildUploadedDocuments(
        existingUploadedData,
        newUploadedFiles,
      );
      return {
        ...state,
        [UPLOADED_FILES]: uploadedFiles,
      };
    }

    case UPDATE_STATE_DELETE_DOCUMENT: {
      const { deletedData } = value;
      const existingUploadedData = [...state[UPLOADED_FILES]];
      const updatedUploadedFiles = existingUploadedData.filter(
        (item) => item.fileName !== deletedData.name,
      );
      return {
        ...state,
        [UPLOADED_FILES]: updatedUploadedFiles,
      };
    }
    case UPDATE_STATE_RELATED_DOCUMENTS: {
      const { incomingCoverages } = value;

      const selectedCoverages = incomingCoverages.reduce((acc, coverage) => {
        const updatedCoverage = newCoverages(acc, coverage);
        return updatedCoverage;
      }, state[SELECTED_COVERAGES]);

      const newDocuments = [...state[DOCUMENTS].files];

      const { documentTypes: rawDocTypes, uploadedFiles } = buildDocumentTypes(
        selectedCoverages,
        state[IS_CURRENT_CLIENT],
        newDocuments,
        state.draft,
      );
      const enCensus = shouldDemoteCensusToOptional(state);
      const documentTypes = enCensus
        ? demoteCensusToOptional(rawDocTypes)
        : rawDocTypes;

      const areValid = documentTypes.every((doc) =>
        doc.coverages.required.every((item) => item.files.length > 0),
      );

      return {
        ...state,
        [SELECTED_COVERAGES]: selectedCoverages,
        [DOCUMENTS]: {
          ...state[DOCUMENTS],
          files: newDocuments,
        },
        [ARE_DOCUMENTS_VALID]: areValid,
        [DOCUMENT_TYPES]: documentTypes,
        [UPLOADED_FILES]: uploadedFiles,
      };
    }

    case EDIT_STATE_RELATED_DOCUMENTS: {
      const {
        incomingCoverages,
        incomingDocs,
        uncheckedCoverages,
        fileName,
        documentTypeName,
      } = value;

      const selectedCoverages = incomingCoverages.reduce((acc, coverage) => {
        const updatedCoverage = newCoverages(acc, coverage);
        return updatedCoverage;
      }, state[SELECTED_COVERAGES]);

      selectedCoverages.map((coverage) => {
        if (uncheckedCoverages.some((uc) => uc.coverageId === coverage.id)) {
          coverage.documents =
            coverage.documents?.filter(
              (doc) =>
                doc.fileName !== fileName || doc.name !== documentTypeName,
            ) || [];
        }
        return coverage;
      });

      const newDocuments = [...state[DOCUMENTS].files, ...incomingDocs];

      const { documentTypes: rawDocTypes, uploadedFiles } = buildDocumentTypes(
        selectedCoverages,
        state[IS_CURRENT_CLIENT],
        newDocuments,
        state.draft,
      );
      const enCensus = shouldDemoteCensusToOptional(state);
      const documentTypes = enCensus
        ? demoteCensusToOptional(rawDocTypes)
        : rawDocTypes;

      const areValid = documentTypes.every((doc) =>
        doc.coverages.required.every((item) => item.files.length > 0),
      );

      return {
        ...state,
        [SELECTED_COVERAGES]: selectedCoverages,
        [DOCUMENTS]: {
          ...state[DOCUMENTS],
          files: newDocuments,
        },
        [ARE_DOCUMENTS_VALID]: areValid,
        [DOCUMENT_TYPES]: documentTypes,
        [UPLOADED_FILES]: uploadedFiles,
      };
    }

    case REMOVE_STATE_RELATED_DOCUMENTS_ALL_COVERAGES: {
      const { fileName } = value;

      const newSelectedCoverages = state[SELECTED_COVERAGES].map((coverage) => {
        if (coverage.documents) {
          coverage.documents = coverage.documents.filter(
            (doc) => doc.fileName !== fileName,
          );
        }
        return coverage;
      });

      const updatedSelectedCoverages = [...newSelectedCoverages];
      const newDocumentFiles = state[DOCUMENTS].files.map((doc) => {
        if (doc.name === fileName && !(doc instanceof File)) {
          doc.type = null;
        }
        return doc;
      });

      const { documentTypes: rawDocTypes, uploadedFiles } = buildDocumentTypes(
        newSelectedCoverages,
        state[IS_CURRENT_CLIENT],
        newDocumentFiles,
        state.draft,
      );
      const enCensus = shouldDemoteCensusToOptional(state);
      const documentTypes = enCensus
        ? demoteCensusToOptional(rawDocTypes)
        : rawDocTypes;

      const areValid = documentTypes.every((doc) =>
        doc.coverages.required.every((item) => item.files.length > 0),
      );

      return {
        ...state,
        [ARE_DOCUMENTS_VALID]: areValid,
        [DOCUMENT_TYPES]: documentTypes,
        [UPLOADED_FILES]: uploadedFiles,
        [SELECTED_COVERAGES]: updatedSelectedCoverages,
      };
    }

    case REMOVE_STATE_RELATED_DOCUMENTS: {
      const { fileName, documentTypeName } = value;

      const newSelectedCoverages = state[SELECTED_COVERAGES].map((coverage) => {
        if (coverage.documents) {
          coverage.documents = coverage.documents.filter(
            (doc) => doc.fileName !== fileName || doc.name !== documentTypeName,
          );
        }
        return coverage;
      });
      const newDocumentFiles = state[DOCUMENTS].files.map((doc) => {
        if (doc.name === fileName && doc.type === documentTypeName) {
          doc.type = null;
        }
        return doc;
      });

      const { documentTypes: rawDocTypes, uploadedFiles } = buildDocumentTypes(
        newSelectedCoverages,
        state[IS_CURRENT_CLIENT],
        newDocumentFiles,
        state.draft,
      );
      const enCensus = shouldDemoteCensusToOptional(state);
      const documentTypes = enCensus
        ? demoteCensusToOptional(rawDocTypes)
        : rawDocTypes;

      const areValid = documentTypes.every((doc) =>
        doc.coverages.required.every((item) => item.files.length > 0),
      );

      return {
        ...state,
        [ARE_DOCUMENTS_VALID]: areValid,
        [DOCUMENT_TYPES]: documentTypes,
        [UPLOADED_FILES]: uploadedFiles,
        [newSelectedCoverages]: newSelectedCoverages,
        [SELECTED_COVERAGES]: newSelectedCoverages,
      };
    }

    case UPDATE_SELECTED_COVERAGE_WITH_DOCUMENTS: {
      const selectedCoverages = newCoverages(state[SELECTED_COVERAGES], value);

      return {
        ...state,
        [SELECTED_COVERAGES]: selectedCoverages,
      };
    }
    case HANDLE_BACK_COVERAGE: {
      const newCoverageIndex =
        state[COVERAGE_INDEX] === 0 ? 0 : state[COVERAGE_INDEX] - 1;
      return {
        ...state,
        [COVERAGE_INDEX]: newCoverageIndex,
      };
    }
    case HANDLE_NEXT_VALIDATE_COVERAGE: {
      const coverage = coverages[value.name]({
        ...value,
        [IS_CURRENT_CLIENT]: state[IS_CURRENT_CLIENT],
      });
      const selectedCoverages = newCoverages(
        state[SELECTED_COVERAGES],
        coverage,
      );
      const isCurrentValid = coverage.isValid;

      return {
        ...state,
        [ARE_COVERAGES_VALID]: areCoveragesValid(selectedCoverages),
        [SELECTED_COVERAGES]: selectedCoverages,
        [COVERAGE_INDEX]: isCurrentValid ? action.index : state[COVERAGE_INDEX],
      };
    }
    case UPDATE_COVERAGE_INDEX: {
      return {
        ...state,
        [COVERAGE_INDEX]: value,
      };
    }
    case UPDATE_SELECTED_COVERAGES: {
      const selectedCoverages = handleCoverageSelection(state, {
        ...value,
        [IS_CURRENT_CLIENT]: state[IS_CURRENT_CLIENT],
        details: initDetailsValues(value.name),
      });
      const { documentTypes: rawDocTypes, uploadedFiles } = buildDocumentTypes(
        selectedCoverages,
        state[IS_CURRENT_CLIENT],
        state[DOCUMENTS].files,
        state.draft,
      );
      const enCensus = shouldDemoteCensusToOptional(state);
      const documentTypes = enCensus
        ? demoteCensusToOptional(rawDocTypes)
        : rawDocTypes;
      const areValid = documentTypes.every((doc) =>
        doc.coverages.required.every((item) => item.files.length > 0),
      );
      return {
        ...state,
        [ARE_COVERAGES_VALID]: areCoveragesValid(selectedCoverages),
        [SELECTED_COVERAGES]: selectedCoverages,
        [ARE_DOCUMENTS_VALID]: areValid,
        [DOCUMENT_TYPES]: documentTypes,
        [UPLOADED_FILES]: uploadedFiles,
      };
    }
    case UPDATE_SELECTED_CARRIERS: {
      return {
        ...state,
        [SELECTED_CARRIERS]: handleCarrierSelection(state, value),
      };
    }
    case UPDATE_CENTRO_SELECTED_CARRIER: {
      const { selectCentro } = value;
      return {
        ...state,
        [SELECTED_CARRIERS]: [],
        [IS_CENTRO_CARRIER_SELECTED]: selectCentro,
      };
    }
    case UPDATE_DOCUMENT: {
      const files = state[DOCUMENTS].files;
      const currentFiles =
        files?.filter((d) => d.name != action.value.name) || [];
      return {
        ...state,
        [DOCUMENTS]: {
          ...state[DOCUMENTS],
          files: [...currentFiles, action.value],
        },
      };
    }
    case SET_DOCUMENTS: {
      return {
        ...state,
        [DOCUMENTS]: {
          ...state[DOCUMENTS],
          files: [...action.value],
        },
      };
    }
    case CLEANUP_DOCUMENTS: {
      return {
        ...state,
        [DOCUMENTS]: {
          files: action.value || [],
        },
      };
    }
    case UPDATE_LAST_MODIFIED: {
      return {
        ...state,
        [LAST_MODIFIED]: value,
      };
    }
    case UPDATE_RFP_ID: {
      return {
        ...state,
        [ID]: value,
      };
    }
    case UPDATE_FIELD: {
      if (!action.name) throw Error("Field name missing");
      const newData = { ...state[FORM_INFO], [action.name]: action.value };

      let clientErrors = null;
      if (rfpInfoValidations[action.name]) {
        clientErrors = {
          [action.name]:
            rfpInfoValidations[action.name](action.value, newData) || null,
        };
      }
      const dependents = dependentValidations[action.name];
      if (dependents) {
        clientErrors = clientErrors || {};
        dependents.forEach((dep) => {
          if (rfpInfoValidations[dep]) {
            clientErrors[dep] =
              rfpInfoValidations[dep](newData[dep], newData) || null;
          }
        });
      }

      return {
        ...state,
        [IS_RFP_FORM_VALID]: validateForm(newData, isRfpFormValid),
        [FORM_INFO]: newData,
        ...(clientErrors ? { [CLIENT_ERROR]: clientErrors } : null),
        // When the EN census flag changes, rebuild document types so the Census
        // upload is immediately promoted/demoted without needing a coverage change.
        ...(action.name === EN_CENSUS_AVAILABLE
          ? (() => {
              const { documentTypes: rawDocTypes, uploadedFiles } =
                buildDocumentTypes(
                  state[SELECTED_COVERAGES] || [],
                  state[IS_CURRENT_CLIENT],
                  state[DOCUMENTS].files,
                  state.draft,
                );
              const isCensusAvailable =
                action.value === "yes" &&
                Boolean(state[FORM_INFO]?.[EN_CENSUS_FEATURE_ENABLED]) &&
                state[FORM_INFO]?.[HAS_BEN_ADMIN_SYSTEM_STATUS] === "1" &&
                (state[IS_CURRENT_CLIENT] === true ||
                  state[FORM_INFO]?.[EN_CENSUS_FOR_PROSPECTS] === true) &&
                state[FORM_INFO]?.[REQUESTED_SYSTEM] === "employeeNavigator";
              const documentTypes = isCensusAvailable
                ? demoteCensusToOptional(rawDocTypes)
                : rawDocTypes;
              const areValid = documentTypes.every((doc) =>
                doc.coverages.required.every((item) => item.files?.length),
              );
              return {
                [DOCUMENT_TYPES]: documentTypes,
                [UPLOADED_FILES]: uploadedFiles,
                [ARE_DOCUMENTS_VALID]: areValid,
              };
            })()
          : null),
      };
    }

    case UPDATE_ADDITIONAL_DOCUMENTS: {
      const currentFiles = state[DOCUMENTS].files;
      const newAdditionalFiles = action.value;
      let filterCurrentFiles = [];

      newAdditionalFiles.forEach((file) => {
        filterCurrentFiles =
          currentFiles?.filter((d) => d.name !== file.name) || [];
      });

      const newAdditionalDocs = newAdditionalFiles.map((file) => file.name);

      return {
        ...state,
        [DOCUMENTS]: {
          ...state[DOCUMENTS],
          files: [...filterCurrentFiles, ...newAdditionalFiles],
        },
        [ADDITIONAL_DOCUMENTS]: [
          ...state[ADDITIONAL_DOCUMENTS],
          ...newAdditionalDocs,
        ],
      };
    }

    case REMOVE_ADDITIONAL_DOCUMENTS: {
      const fileName = action.value.name;
      const uploadedFiles = state[UPLOADED_FILES].map((file) => file.fileName);
      const newAdditionalFiles = state[ADDITIONAL_DOCUMENTS].filter(
        (doc) => doc !== fileName,
      );
      if (!uploadedFiles.includes(fileName)) {
        const filteredDocuments = state[DOCUMENTS].files.filter(
          (doc) => doc.name !== fileName,
        );
        return {
          ...state,
          [ADDITIONAL_DOCUMENTS]: newAdditionalFiles,
          [DOCUMENTS]: {
            ...state[DOCUMENTS],
            files: filteredDocuments,
          },
        };
      }
      return {
        ...state,
        [ADDITIONAL_DOCUMENTS]: newAdditionalFiles,
      };
    }

    default:
      return {
        ...state,
        ...value,
      };
  }
};

const validateForm = (data, validationType) =>
  Object.keys(data).every((key) =>
    validationType[key] ? validationType[key](data[key], data) : true,
  );

export default RfpReducer;
